import {createModel} from '@rematch/core';
import {RootModel} from './index';
import {IMessage, IUser} from '../../Constants/Interfaces';

import {auth, db} from '../../Firebase/main';
import {sendPrivateMessage, sendPublicMessage} from '../../Messages';
import {isUserId} from '../../Users';
import {clean} from '../../Utils/profanityFilter';


interface messages {
    private_messages: { [uid: string]: { [message: string]: IMessage } };
    public_messages: { [room: string]: { [message: string]: IMessage } };
}


const init: messages = {
    private_messages: {},
    public_messages: {},
};

export const Messages = createModel<RootModel>()({
    state: init,
    reducers: {
        update(state, payload: Partial<messages>) {
            return {...state, ...payload};
        },
        updatePrivateMessages(state, payload: typeof init.private_messages) {
            return {...state, private_messages: {...state.private_messages, ...payload}};
        },
        updatePublicMessages(state, payload: typeof init.public_messages) {
            return {...state, public_messages: {...state.public_messages, ...payload}};
        },
        updateRoomMessages(state, payload: { roomId: string; messages: { [message: string]: IMessage } }) {
            return {
                ...state,
                public_messages: {
                    ...state.public_messages,
                    [payload.roomId]: {...state.public_messages[payload.roomId], ...payload.messages},
                },
            };
        },
        deletePublicMessage(state, payload: { roomId: string, messageId: string }) {
            const copy = Object.assign({}, state);
            delete copy.public_messages[payload.roomId][payload.messageId];
            return copy;
        },
    },
    effects: (dispatch) => ({
        async subscribeToPrivateMessages(_, state): Promise<any> {

            if (!auth.currentUser) return;

            //Reference
            const privateMessagesRef = db.ref(`private_messages/${auth.currentUser.uid}`);

            //Get initial private messages
            privateMessagesRef.on('value', (messagesSnapshot) => {
                if (!messagesSnapshot.exists()) return;

                messagesSnapshot.forEach((userNode) => {
                    const userId = userNode.key;
                    if (userId) {
                        //Load users
                        if (!state.users.data[userId]) {
                            db.ref(`users/${userNode.key}`).get().then((user) => {
                                if (!user.exists()) return;
                                const userData = user.val() as IUser;
                                dispatch.users.updateUsers({[userData.uid]: userData});
                            });
                        }
                    }
                });
                dispatch.messages.updatePrivateMessages(messagesSnapshot.val());
            });
        },

        async handleRoomSelect(selectedRoomId: string, state) {
            //Detach listener from the previous room
            db.ref(`public_messages/${state.main.selectedRoom}`).off();

            const selectedRoomMessagesReference = db.ref(`public_messages/${selectedRoomId}`);
            const loadedMessages = state.messages.public_messages[selectedRoomId];
            const loadedMessagesKeys = Object.keys(loadedMessages || {});

            const {
                main: {update},
                messages: {updatePublicMessages, updateRoomMessages},
            } = dispatch;

            //Dispatch selected room id to STATE
            update({selectedId: selectedRoomId, selectedRoom: selectedRoomId, viewProfile: false});

            //Check if no messages loaded and pull last 25 messages
            if (!loadedMessages) {
                await selectedRoomMessagesReference
                    .limitToLast(25)
                    .get()
                    .then(async (snapshot) => {
                        if (snapshot.exists()) {
                            const data = snapshot.val();
                            const arr = Object.values(data) as IMessage[];
                            await arr
                                .reduce<string[]>((res, val) => res.includes(val.sender) || val.sender_name === 'Guest' ? res : [...res, val.sender], [])
                                .forEach((sender) => {

                                    if (!state.users.data[sender]) {
                                        db.ref(`users/${sender}`).get().then((user) => user.val()).then((data) => {
                                            dispatch.users.updateUsers({[sender]: data});
                                        });
                                    }
                                });
                            updatePublicMessages({[selectedRoomId]: snapshot.val()});
                        }
                    });
            }
            //Otherwise pull all recent messages
            else {
                const lastLoadedMessageTimestamp = loadedMessagesKeys[loadedMessagesKeys.length - 1];
                await selectedRoomMessagesReference
                    .orderByKey()
                    .startAfter(lastLoadedMessageTimestamp)
                    .get()
                    .then((snapshot) => {
                        if (snapshot.exists()) {
                            updateRoomMessages({roomId: selectedRoomId, messages: snapshot.val()});
                        }
                    });
            }

            //Attach a listener to the newly selected room
            selectedRoomMessagesReference
                .limitToLast(1)
                .on('child_added', (snapshot) => {
                    if (snapshot.exists()) {
                        const messageData = snapshot.val();
                        const messageKey = messageData.timestamp.toString();
                        //Check if message hasn't been received and dispatch it to STATE
                        if (!loadedMessagesKeys.includes(messageKey)) {
                            updateRoomMessages({roomId: selectedRoomId, messages: {[messageKey]: messageData}});
                        }
                    }
                });

            selectedRoomMessagesReference
                .on('child_changed', (snap) => {
                    const data = snap.val();
                    if (data) {
                        updateRoomMessages({roomId: selectedRoomId, messages: {[data.timestamp]: data}});
                    }
                });

            selectedRoomMessagesReference
                .on('child_removed', (snap) => {
                    const data = snap.val();
                    if (data) {
                        dispatch.messages.deletePublicMessage({roomId: selectedRoomId, messageId: data.timestamp});
                    }
                });
        },

        async sendMessage(payload: { message: string; receiver: string }, state) {
            const myId = auth.currentUser?.uid;
            if (!myId) {
                console.error('Failed sending message. User not authenticated!');
                return;
            }

            const myName = state.users.data[myId].name;

            const {receiver, message} = payload;


            const badWords = await db.ref('filters/seed_words').get().then((snap) => snap.val()) as string[];

            const data = {
                receiver,
                message: clean(message, Object.values(badWords).filter(e => e)),
                sender: myId,
                sender_name: myName,
            };

            //Send Private Message
            if (isUserId(receiver)) {
                await sendPrivateMessage(data);
            }
            //Send Public Message
            else {
                await sendPublicMessage(data);
            }
        },
    }),
});
